import React, { useState, useEffect } from 'react';
import {
  Box,Typography,Paper,Table,TableBody,TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  Divider,
  Skeleton,
  Fade,
  Slide,
  Badge,
  Tooltip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  useTheme
} from '@material-ui/core';
import { makeStyles } from  '@material-ui/styles';
import {
  Check as ApproveIcon,
  Close as DeclineIcon,
  Visibility as ViewIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  MonetizationOn as PaymentIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon
} from '@material-ui/icons';
import moment from 'moment';
import admin_api from '../../../api/admin';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';

const DRAWER_WIDTH = 280;
const COLLAPSE_WIDTH = 102;

// Custom styles using makeStyles
const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
    padding: '20px',
    color: '#ffffff',
    marginTop: '80px',
    marginRight: '24px',
    transition: 'all 0.3s ease',
  },
  paper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid #2d3748',
  },
  card: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    border: '1px solid #2d3748',
    height: '100%',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: theme.spacing(1),
      '& fieldset': {
        borderColor: '#2d3748',
      },
      '&:hover fieldset': {
        borderColor: '#4a5568',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#06b6d4',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#a0aec0',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#06b6d4',
    },
    '& .MuiOutlinedInput-input': {
      color: '#e2e8f0',
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: '#06b6d4',
    },
  },
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1a2235',
      color: '#ffffff',
      borderRadius: theme.spacing(2),
      border: '1px solid #2d3748',
    },
  },
  headerCard: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: 'white',
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(6, 182, 212, 0.3)',
  },
  successButton: {
    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
    },
  },
  errorButton: {
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    minWidth: 120,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
      background: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)',
    },
  },
  tableHead: {
    backgroundColor: '#1e293b',
    color: '#06b6d4',
    fontWeight: 'bold',
    borderBottom: '2px solid #2d3748'
  },
  tableRow: {
    '& .MuiTableCell-root': {
      color: '#e2e8f0',
      borderColor: '#2d3748'
    },
    '&:hover': {
      backgroundColor: 'rgba(6, 182, 212, 0.05)'
    }
  },
  pagination: {
    color: '#e2e8f0',
    borderTop: '1px solid #2d3748',
    '& .MuiTablePagination-selectIcon': {
      color: '#e2e8f0'
    },
    '& .MuiTablePagination-actions button': {
      color: '#e2e8f0'
    }
  },
  avatar: {
    backgroundColor: '#06b6d4',
    width: 32,
    height: 32,
    fontSize: '0.8rem'
  },
  chip: {
    backgroundColor: 'rgba(6, 182, 212, 0.1)',
    color: '#06b6d4',
    borderColor: '#06b6d4',
    border: '1px solid'
  },
  alertSuccess: {
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    color: '#22c55e',
    border: '1px solid rgba(34, 197, 94, 0.2)'
  },
  alertWarning: {
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    color: '#f59e0b',
    border: '1px solid rgba(245, 158, 11, 0.2)'
  }
}));

// Utility functions
const calculateTotalHours = (timeFrom, timeTo) => {
  if (!timeFrom || !timeTo) return '0';
  
  const [fromHours, fromMinutes] = timeFrom.split(":").map(Number);
  const [toHours, toMinutes] = timeTo.split(":").map(Number);

  const from = new Date(0, 0, 0, fromHours, fromMinutes);
  const to = new Date(0, 0, 0, toHours, toMinutes);

  const diff = (to - from) / (1000 * 60 * 60);
  return diff.toFixed(2);
};

const getProperty = (obj, propNames, defaultValue = '') => {
  if (!obj) return defaultValue;
  for (const prop of propNames) {
    if (obj[prop] !== undefined && obj[prop] !== null) {
      return obj[prop];
    }
  }
  return defaultValue;
};

const formatDateToPH = (dateString) => {
  if (!dateString) return 'N/A';
  return moment(dateString).utcOffset('+08:00').format('MMM D, YYYY');
};

const convertTo12Hour = (timeString) => {
  if (!timeString) return '';
  
  if (timeString.includes('AM') || timeString.includes('PM')) {
    return timeString;
  }
  
  const [hours, minutes] = timeString.split(':').map(Number);
  
  if (isNaN(hours) || isNaN(minutes)) {
    return timeString;
  }
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export default function ApprovalPage() {
  const classes = useStyles();
  const theme = useTheme(); // Add this line to access theme
  const [pendingPayments, setPendingPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const { isCollapse } = useCollapseDrawer();
  
  // Calculate container margins
  const containerStyle = {
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  const fetchPendingPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await admin_api.get_pending_payments(page + 1, rowsPerPage);
      
      if (response && response.data && response.data.success) {
        const payments = response.data.data || [];
        
        setPendingPayments(payments);
        setFilteredPayments(payments);
        setTotalCount(response.data.pagination?.totalRecords || payments.length);
      } else {
        setError(response?.data?.message || 'Failed to fetch pending payments');
        setPendingPayments([]);
        setFilteredPayments([]);
      }
    } catch (error) {
      console.error('Error in fetchPendingPayments:', error);
      setError('Failed to fetch pending payments. Please try again.');
      setPendingPayments([]);
      setFilteredPayments([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter payments based on search and payment method
  useEffect(() => {
    let filtered = [...pendingPayments];
    
    if (searchQuery) {
      filtered = filtered.filter(payment => 
        payment.transaction?.booking_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.booking_id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.transaction?.court_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.court_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        payment.payer_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(payment => 
        payment.payment_method === paymentMethodFilter
      );
    }
    
    setFilteredPayments(filtered);
  }, [pendingPayments, searchQuery, paymentMethodFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setOpenDetailsDialog(true);
  };

  const handleApproveClick = (payment) => {
    setSelectedPayment(payment);
    setAdminNotes('');
    setOpenApproveDialog(true);
  };

  const handleDeclineClick = (payment) => {
    setSelectedPayment(payment);
    setAdminNotes('');
    setOpenDeclineDialog(true);
  };

  const handleApproveConfirm = async () => {
    if (!selectedPayment) return;
    
    setActionLoading(true);
    try {
      const paymentId = selectedPayment.id || selectedPayment.transaction_id;
      
      if (!paymentId) {
        throw new Error('Payment ID is missing from the payment object');
      }
      
      const requestData = {
        paymentId: parseInt(paymentId, 10),
        status: 'verified',
        adminNotes: adminNotes || ''
      };
      
      const response = await admin_api.verify_payment(requestData);
      
      if (response && response.data && response.data.success) {
        setSuccessMessage('Payment approved successfully');
        setOpenApproveDialog(false);
        fetchPendingPayments();
      } else {
        setError(response?.data?.message || 'Failed to approve payment');
      }
    } catch (error) {
      console.error('Error in handleApproveConfirm:', error);
      setError(error.message || 'Failed to approve payment. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclineConfirm = async () => {
    if (!selectedPayment) return;
    
    if (!adminNotes) {
      setError('Please provide a reason for declining the payment');
      return;
    }
    
    setActionLoading(true);
    try {
      const paymentId = selectedPayment.id || selectedPayment.transaction_id;
      
      if (!paymentId) {
        throw new Error('Payment ID is missing');
      }
      
      const requestData = {
        paymentId: parseInt(paymentId, 10),
        status: 'rejected',
        adminNotes: adminNotes
      };
      
      const response = await admin_api.verify_payment(requestData);
      
      if (response && response.data && response.data.success) {
        setSuccessMessage('Payment declined successfully');
        setOpenDeclineDialog(false);
        fetchPendingPayments();
      } else {
        setError(response?.data?.message || 'Failed to decline payment');
      }
    } catch (error) {
      console.error('Error in handleDeclineConfirm:', error);
      setError(error.message || 'Failed to decline payment. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const clearMessages = () => {
    setSuccessMessage('');
    setError(null);
  };
  
  // Calculate summary statistics
  const summaryStats = {
    pending: filteredPayments.length,
    todaySubmissions: filteredPayments.filter(p => {
      const createdAt = getProperty(p, ['created_at', 'createdAt', 'submission_date']);
      if (!createdAt) return false;
      
      const todayPH = new Date().toLocaleString('en-US', {timeZone: 'Asia/Manila'});
      const today = new Date(todayPH);
      
      const date = new Date(createdAt);
      const datePH = new Date(date.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
      
      return datePH.getDate() === today.getDate() && 
             datePH.getMonth() === today.getMonth() && 
             datePH.getFullYear() === today.getFullYear();
    }).length,
    totalValue: filteredPayments.reduce((sum, p) => 
      sum + parseFloat(getProperty(p, ['amount', 'payment_amount'], 0)), 0
    )
  };

  useEffect(() => {
    fetchPendingPayments();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        clearMessages();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  return (
    <Box className={classes.container} style={containerStyle}>
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" style={{ backgroundColor: '#22c55e' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" style={{ backgroundColor: '#ef4444' }}>
          {error}
        </Alert>
      </Snackbar>

      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <Card className={classes.headerCard}>
            <CardContent style={{ padding: theme.spacing(4) }}>
              <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 8 }}>
                Payment Approval
              </Typography>
              <Typography variant="h6" style={{ opacity: 0.9 }}>
                Review and approve pending payment submissions
              </Typography>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={3} style={{ marginBottom: theme.spacing(4) }}>
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1000}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <Badge badgeContent={summaryStats.pending} color="error" max={99}>
                      <AssignmentIcon style={{ fontSize: 40, color: '#06b6d4', marginBottom: 16 }} />
                    </Badge>
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Pending Approvals
                    </Typography>
                    <Typography variant="h4" style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      {summaryStats.pending}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Payments awaiting review
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1200}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <ScheduleIcon style={{ fontSize: 40, color: '#f59e0b', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Today's Submissions
                    </Typography>
                    <Typography variant="h4" style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                      {summaryStats.todaySubmissions}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Payments submitted today
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Slide direction="up" in timeout={1400}>
                <Card className={classes.card}>
                  <CardContent style={{ textAlign: 'center', padding: theme.spacing(3) }}>
                    <MoneyIcon style={{ fontSize: 40, color: '#22c55e', marginBottom: 16 }} />
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Total Value
                    </Typography>
                    <Typography variant="h4" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                      ₱{summaryStats.totalValue.toLocaleString()}
                    </Typography>
                    <Typography style={{ color: '#a0aec0', marginTop: 8 }}>
                      Combined pending value
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          </Grid>

          {/* Filters and Search */}
          <Paper className={classes.paper} style={{ padding: theme.spacing(3), marginBottom: theme.spacing(4) }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search payments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={classes.textField}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#06b6d4' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel style={{ color: '#a0aec0' }}>Payment Method</InputLabel>
                  <Select
                    value={paymentMethodFilter}
                    onChange={(e) => setPaymentMethodFilter(e.target.value)}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#e2e8f0',
                    }}
                    inputProps={{
                      style: {
                        color: '#e2e8f0',
                      }
                    }}
                  >
                    <MenuItem value="all">All Methods</MenuItem>
                    <MenuItem value="gcash">GCash</MenuItem>
                    <MenuItem value="paymaya">PayMaya</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchPendingPayments}
                    disabled={loading}
                    style={{
                      color: '#e2e8f0',
                      borderColor: '#4a5568',
                    }}
                  >
                    Refresh
                  </Button>
                  <Typography 
                    variant="body2" 
                    style={{ color: '#a0aec0', alignSelf: 'center' }}
                  >
                    {filteredPayments.length} payment{filteredPayments.length !== 1 ? 's' : ''} found
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Payments Table */}
          <Slide direction="up" in timeout={1600}>
            <TableContainer component={Paper} className={classes.paper}>
              {loading ? (
                <Box style={{ padding: theme.spacing(4) }}>
                  {[...Array(5)].map((_, index) => (
                    <Box key={index} style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
                      <Skeleton variant="circle" width={40} height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="30%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="40%" height={40} style={{ backgroundColor: '#2d3748' }} />
                      <Skeleton variant="text" width="20%" height={40} style={{ backgroundColor: '#2d3748' }} />
                    </Box>
                  ))}
                </Box>
              ) : filteredPayments.length === 0 ? (
                <Box style={{ padding: theme.spacing(6), textAlign: 'center' }}>
                  <AssignmentIcon style={{ fontSize: 60, color: '#4a5568', marginBottom: 16 }} />
                  <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: 8 }}>
                    No pending payments found
                  </Typography>
                  <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: 24 }}>
                    All payments have been processed or no payments match your search criteria.
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={fetchPendingPayments}
                    style={{
                      color: '#e2e8f0',
                      borderColor: '#4a5568',
                    }}
                  >
                    Refresh List
                  </Button>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHead}>Booking</TableCell>
                      <TableCell className={classes.tableHead}>Court & Date</TableCell>
                      <TableCell className={classes.tableHead}>Amount</TableCell>
                      <TableCell className={classes.tableHead}>Payment Method</TableCell>
                      <TableCell className={classes.tableHead}>Submitted</TableCell>
                      <TableCell className={classes.tableHead} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((payment, index) => {
                      const bookingRef = getProperty(payment, ['transaction.booking_id', 'booking_id', 'bookingRef'], 'N/A');
                      const courtName = getProperty(payment, ['transaction.court_name', 'court_name', 'courtName'], 'Unknown Court');
                      const reservationDate = getProperty(payment, ['transaction.reservation_date', 'reservation_date', 'reservationDate']);
                      const timeFrom = getProperty(payment, ['transaction.time_from', 'time_from', 'timeFrom']);
                      const timeTo = getProperty(payment, ['transaction.time_to', 'time_to', 'timeTo']);
                      const amount = parseFloat(getProperty(payment, ['amount', 'payment_amount'], 0));
                      const paymentMethod = getProperty(payment, ['payment_method', 'paymentMethod'], 'Unknown');
                      const createdAt = getProperty(payment, ['created_at', 'createdAt']);
                      const payerName = getProperty(payment, ['payer_name', 'payerName'], 'Unknown');
                      
                      return (
                        <Fade in timeout={300 + index * 100} key={payment.id || payment.transaction_id}>
                          <TableRow className={classes.tableRow}>
                            <TableCell>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <Avatar className={classes.avatar}>
                                  {payerName.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" style={{ fontWeight: 'bold' }}>
                                    {bookingRef}
                                  </Typography>
                                  <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                    {payerName}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" style={{ fontWeight: 'bold', marginBottom: 4 }}>
                                  {courtName}
                                </Typography>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <EventIcon style={{ fontSize: 14, color: '#06b6d4' }} />
                                  <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                    {reservationDate ? formatDateToPH(reservationDate) : 'N/A'}
                                  </Typography>
                                </Box>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <TimeIcon style={{ fontSize: 14, color: '#06b6d4' }} />
                                  <Typography variant="caption" style={{ color: '#a0aec0' }}>
                                    {timeFrom && timeTo ? `${convertTo12Hour(timeFrom)} - ${convertTo12Hour(timeTo)}` : 'Time not specified'}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="h6" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                                ₱{amount.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="h6" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                                ₱{amount.toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={paymentMethod} 
                                size="small" 
                                className={classes.chip}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" style={{ color: '#a0aec0' }}>
                                {createdAt ? moment(createdAt).utcOffset('+08:00').fromNow() : 'Unknown'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                <Tooltip title="View Details">
                                  <IconButton 
                                    style={{ color: '#06b6d4' }}
                                    onClick={() => handleViewDetails(payment)}
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Approve Payment">
                                  <IconButton 
                                    style={{ color: '#22c55e' }}
                                    onClick={() => handleApproveClick(payment)}
                                  >
                                    <ApproveIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Decline Payment">
                                  <IconButton 
                                    style={{ color: '#ef4444' }}
                                    onClick={() => handleDeclineClick(payment)}
                                  >
                                    <DeclineIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        </Fade>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              
              {filteredPayments.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredPayments.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  className={classes.pagination}
                />
              )}
            </TableContainer>
          </Slide>
        </Box>
      </Fade>

      {/* Payment Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="lg"
        fullWidth
        classes={{ paper: classes.dialog }}
      >
        {selectedPayment && (
          <>
            <DialogTitle style={{ 
              borderBottom: '1px solid #2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: 16
            }}>
              <ViewIcon style={{ color: '#06b6d4' }} />
              Payment Details
            </DialogTitle>
            <DialogContent dividers style={{ padding: theme.spacing(4) }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3), height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#06b6d4', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Payment Information
                    </Typography>
                    
                    <Box style={{ marginBottom: 24 }}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Typography variant="subtitle2">Reference Number:</Typography>
                        <Typography variant="body1" style={{ fontWeight: 'medium' }}>
                          {getProperty(selectedPayment, ['reference_number', 'referenceNumber']) || 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Typography variant="subtitle2">Payment Method:</Typography>
                        <Chip 
                          label={getProperty(selectedPayment, ['payment_method', 'paymentMethod']) || 'Unknown'} 
                          size="small" 
                          className={classes.chip}
                        />
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Typography variant="subtitle2">Payer Name:</Typography>
                        <Typography variant="body1">
                          {getProperty(selectedPayment, ['payer_name', 'payerName']) || 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                        <Typography variant="subtitle2">Phone:</Typography>
                        <Typography variant="body1">
                          {getProperty(selectedPayment, ['payer_phone', 'payerPhone']) || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Submitted:</Typography>
                      <Box style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        backgroundColor: 'rgba(6, 182, 212, 0.1)', 
                        padding: 12, 
                        borderRadius: 4,
                        border: '1px solid rgba(6, 182, 212, 0.2)'
                      }}>
                        <EventIcon style={{ marginRight: 8, fontSize: 18, color: '#06b6d4' }} />
                        <Typography variant="body2" style={{ color: '#e2e8f0' }}>
                          {moment(getProperty(selectedPayment, ['created_at', 'createdAt'])).utcOffset('+08:00').format('MMMM D, YYYY [at] h:mm A')}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3), height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ 
                      color: '#06b6d4', 
                      fontWeight: 'bold', 
                      borderBottom: '1px solid #2d3748', 
                      paddingBottom: 8, 
                      marginBottom: 16 
                    }}>
                      Booking Details
                    </Typography>
                    
                    <Box style={{ marginBottom: 16 }}>
                      {(() => {
                        const reservationDate = getProperty(selectedPayment, ['transaction.reservation_date', 'reservation_date', 'reservationDate']);
                        const timeFrom = getProperty(selectedPayment, ['transaction.time_from', 'time_from', 'timeFrom']);
                        const timeTo = getProperty(selectedPayment, ['transaction.time_to', 'time_to', 'timeTo']);
                        const courtName = getProperty(selectedPayment, ['transaction.court_name', 'court_name', 'courtName'], 'Unknown Court');
                        
                        return (
                          <>
                            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                              <EventIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                              <Typography>
                                <strong>Date:</strong> {reservationDate ? formatDateToPH(reservationDate) : 'N/A'}
                              </Typography>
                            </Box>
                            
                            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                              <TimeIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                              <Typography>
                                <strong>Time:</strong> {timeFrom && timeTo ? `${convertTo12Hour(timeFrom)} - ${convertTo12Hour(timeTo)}` : 'Time not specified'}
                              </Typography>
                            </Box>
                            
                            <Box style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                              <LocationIcon style={{ marginRight: 8, color: '#06b6d4' }} />
                              <Typography>
                                <strong>Court:</strong> {courtName}
                              </Typography>
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                    
                    <Box style={{ paddingTop: 16, borderTop: '1px solid #2d3748', marginTop: 16 }}>
                      <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 'bold', color: '#06b6d4' }}>
                        Financial Breakdown
                      </Typography>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={7}>
                          <Typography>Amount Paid:</Typography>
                        </Grid>
                        <Grid item xs={5} style={{ textAlign: 'right' }}>
                          <Typography style={{ fontWeight: 'bold', color: '#22c55e' }}>
                            {(() => {
                              const amount = parseFloat(getProperty(selectedPayment, ['amount', 'payment_amount'], 0));
                              return `₱${amount.toLocaleString()}`;
                            })()}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper className={classes.paper} style={{ padding: theme.spacing(3) }}>
                    <Typography variant="h6" gutterBottom style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      Payment Screenshot
                    </Typography>
                    
                    {(() => {
                      const screenshotUrl = selectedPayment.screenshot_urls || selectedPayment.screenshot;
                      
                      if (screenshotUrl) {
                        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:7002';
                        const imageUrl = screenshotUrl.startsWith('http') 
                          ? screenshotUrl 
                          : `${baseUrl}${screenshotUrl.startsWith('/') ? '' : '/'}${screenshotUrl}`;
                        
                        return (
                          <Box style={{ textAlign: 'center', marginTop: 16 }}>
                            <img 
                              src={imageUrl}
                              alt="Payment Screenshot" 
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: '400px', 
                                border: '1px solid #2d3748',
                                borderRadius: '8px'
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIE5vdCBBdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                              }}
                            />
                          </Box>
                        );
                      } else {
                        return (
                          <Box style={{ 
                            textAlign: 'center', 
                            padding: theme.spacing(4), 
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: 4,
                            border: '1px dashed #2d3748'
                          }}>
                            <Typography style={{ color: '#a0aec0' }}>
                              No screenshot available
                            </Typography>
                          </Box>
                        );
                      }
                    })()}
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748', gap: 16 }}>
              <Button 
                onClick={() => setOpenDetailsDialog(false)}
                style={{ color: '#a0aec0' }}
              >
                Close
              </Button>
              <Button 
                className={classes.successButton}
                variant="contained"
                startIcon={<ApproveIcon />}
                onClick={() => {
                  setOpenDetailsDialog(false);
                  handleApproveClick(selectedPayment);
                }}
              >
                Approve
              </Button>
              <Button 
                className={classes.errorButton}
                variant="contained"
                startIcon={<DeclineIcon />}
                onClick={() => {
                  setOpenDetailsDialog(false);
                  handleDeclineClick(selectedPayment);
                }}
              >
                Decline
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      {/* Approve Dialog */}
      <Dialog
        open={openApproveDialog}
        onClose={() => !actionLoading && setOpenApproveDialog(false)}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialog }}
      >
        <DialogTitle style={{ 
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          color: '#22c55e'
        }}>
          <ApproveIcon />
          Approve Payment
        </DialogTitle>
        <DialogContent style={{ padding: theme.spacing(3) }}>
          <Alert 
            severity="success" 
            className={classes.alertSuccess}
          >
            Are you sure you want to approve this payment? This will confirm the booking and notify the customer.
          </Alert>
          
          <TextField
            fullWidth
            label="Admin Notes (Optional)"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={3}
            variant="outlined"
            disabled={actionLoading}
            className={classes.textField}
            placeholder="Add any notes about this approval..."
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748', gap: 16 }}>
          <Button 
            onClick={() => setOpenApproveDialog(false)} 
            disabled={actionLoading}
            style={{ color: '#a0aec0' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleApproveConfirm} 
            className={classes.successButton}
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <ApproveIcon />}
          >
            {actionLoading ? 'Approving...' : 'Approve Payment'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Decline Dialog */}
      <Dialog
        open={openDeclineDialog}
        onClose={() => !actionLoading && setOpenDeclineDialog(false)}
        maxWidth="sm"
        fullWidth
        classes={{ paper: classes.dialog }}
      >
        <DialogTitle style={{ 
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          color: '#ef4444'
        }}>
          <DeclineIcon />
          Decline Payment
        </DialogTitle>
        <DialogContent style={{ padding: theme.spacing(3) }}>
          <Alert 
            severity="warning" 
            className={classes.alertWarning}
          >
            Please provide a clear reason for declining this payment. This will be sent to the customer.
          </Alert>
          
          <TextField
            fullWidth
            label="Reason for Declining *"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            multiline
            rows={4}
            variant="outlined"
            required
            disabled={actionLoading}
            error={openDeclineDialog && !adminNotes}
            helperText={openDeclineDialog && !adminNotes ? 'A reason is required to decline a payment' : 'This message will be sent to the customer'}
            className={classes.textField}
            placeholder="e.g., Screenshot is unclear, payment amount doesn't match, etc."
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions style={{ padding: theme.spacing(3), borderTop: '1px solid #2d3748', gap: 16 }}>
          <Button 
            onClick={() => setOpenDeclineDialog(false)} 
            disabled={actionLoading}
            style={{ color: '#a0aec0' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeclineConfirm} 
            className={classes.errorButton}
            variant="contained"
            disabled={actionLoading || !adminNotes}
            startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <DeclineIcon />}
          >
            {actionLoading ? 'Declining...' : 'Decline Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}